/**
 * `<DbCredsForm>` — connect a customer database (Postgres / MySQL / MongoDB).
 *
 * Flow: pick engine + enter read-only creds → "Test connection"
 * (`POST /api/onboarding/validate-db`, stateless live check, shows the table
 * list) → "Save" persists via the existing `POST /api/connectors` path (the
 * adapter UUID is resolved from the catalog by contract id).
 *
 * validate-db may be absent on this branch (it's WP-2): a 404 surfaces as an
 * `AgentsApiError` with status 404, which we treat as "live testing
 * unavailable" and still allow Save.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AgentsApiError } from "../../agents/api/client";
import { formatApiError } from "../../lib/format-error";
import { StatusPill } from "../../components/status-pill";
import { useAdapters, useCreateConnector } from "../../tables/api/connectors";
import {
  useValidateDb,
  type DbSchemaEntry,
  type DbValidationRequest,
  type DbValidationResult,
} from "../api/onboarding";

type Engine = "postgres" | "mysql" | "mongodb";

const ENGINES: Engine[] = ["postgres", "mysql", "mongodb"];
const DEFAULT_PORT: Record<Engine, string> = {
  postgres: "5432",
  mysql: "3306",
  mongodb: "27017",
};

export function DbCredsForm({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("onboarding");

  const adapters = useAdapters(slug);
  const validate = useValidateDb();
  const create = useCreateConnector(slug);

  const [engine, setEngine] = useState<Engine>("postgres");
  const [name, setName] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState(DEFAULT_PORT.postgres);
  const [database, setDatabase] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [ssl, setSsl] = useState(true);

  const [tested, setTested] = useState<DbValidationResult | null>(null);
  const [testUnavailable, setTestUnavailable] = useState(false);

  function reset() {
    setEngine("postgres");
    setName("");
    setHost("");
    setPort(DEFAULT_PORT.postgres);
    setDatabase("");
    setUsername("");
    setPassword("");
    setSsl(true);
    setTested(null);
    setTestUnavailable(false);
  }

  function onEngineChange(next: Engine) {
    setEngine(next);
    setPort(DEFAULT_PORT[next]);
    setTested(null);
  }

  const credsComplete =
    host.trim() !== "" &&
    port.trim() !== "" &&
    database.trim() !== "" &&
    username.trim() !== "" &&
    password !== "";

  function buildValidateBody(): DbValidationRequest {
    return {
      engine,
      host: host.trim(),
      port: Number(port),
      database: database.trim(),
      username: username.trim(),
      password,
      ssl,
    };
  }

  function runTest() {
    setTested(null);
    validate.mutate(buildValidateBody(), {
      onSuccess: (res) => {
        setTested(res);
        if (res.ok) {
          toast.success(
            res.schema && res.schema.length > 0
              ? t("db.testOk", { count: res.schema.length })
              : t("db.testOkNoTables"),
          );
        }
      },
      onError: (err) => {
        // validate-db not deployed on this branch → allow Save without a live check.
        if (err instanceof AgentsApiError && err.status === 404) {
          setTestUnavailable(true);
          return;
        }
        toast.error(formatApiError(err));
      },
    });
  }

  function runSave() {
    const adapterId = adapters.data?.find((a) => a.id === engine)?.id;
    if (!adapterId) {
      toast.error(formatApiError(new Error(t("db.saveError"))));
      return;
    }
    create.mutate(
      {
        adapterId,
        name: name.trim() || `${t(`db.engines.${engine}`)} — ${database.trim()}`,
        credentials: {
          host: host.trim(),
          port: port.trim(),
          database: database.trim(),
          username: username.trim(),
          password,
          ssl: String(ssl),
        },
        // Databases sync one-way on a default cadence; the engine owns the rest.
        frequency: "1h",
        direction: "in",
      },
      {
        onSuccess: () => {
          toast.success(t("db.savedToast"));
          reset();
          onOpenChange(false);
        },
        onError: (err) => toast.error(formatApiError(err)),
      },
    );
  }

  const errorCopy = (code: DbValidationResult["errorCode"]) => t(`db.errors.${code ?? "UNKNOWN"}`);

  return (
    <Dialog open={open} onOpenChange={(o) => !create.isPending && onOpenChange(o)}>
      <DialogContent data-testid="onboarding-db-form">
        <DialogHeader>
          <DialogTitle>{t("db.title")}</DialogTitle>
          <DialogDescription>{t("db.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="db-engine">{t("db.engineLabel")}</Label>
            <Select value={engine} onValueChange={(v) => onEngineChange(v as Engine)}>
              <SelectTrigger id="db-engine">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENGINES.map((e) => (
                  <SelectItem key={e} value={e}>
                    {t(`db.engines.${e}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="db-name">{t("db.nameLabel")}</Label>
            <Input
              id="db-name"
              value={name}
              placeholder={t("db.namePlaceholder")}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="db-host">{t("db.fields.host")}</Label>
              <Input
                id="db-host"
                value={host}
                onChange={(e) => {
                  setHost(e.target.value);
                  setTested(null);
                }}
                data-testid="onboarding-db-host"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="db-port">{t("db.fields.port")}</Label>
              <Input
                id="db-port"
                inputMode="numeric"
                value={port}
                onChange={(e) => setPort(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="db-database">{t("db.fields.database")}</Label>
            <Input
              id="db-database"
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="db-username">{t("db.fields.username")}</Label>
              <Input
                id="db-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="db-password">{t("db.fields.password")}</Label>
              <Input
                id="db-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="onboarding-db-password"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch id="db-ssl" checked={ssl} onCheckedChange={setSsl} />
            <Label htmlFor="db-ssl">{t("db.fields.ssl")}</Label>
          </div>

          {testUnavailable ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="onboarding-db-test-unavailable"
            >
              {t("db.unavailable")}
            </p>
          ) : null}

          {tested && tested.ok ? (
            <div
              className="space-y-2 rounded-lg border border-border p-3"
              data-testid="onboarding-db-tables"
            >
              <div className="flex items-center gap-2">
                <StatusPill tone="success">{t("status.connected")}</StatusPill>
                <span className="text-sm text-muted-foreground">
                  {t("db.tablesFound")}: {tested.schema?.length ?? 0}
                </span>
              </div>
              {tested.schema && tested.schema.length > 0 ? (
                <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                  {tested.schema.map((s: DbSchemaEntry) => (
                    <StatusPill key={s.name} tone="neutral" className="font-mono">
                      {s.name}
                    </StatusPill>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {tested && !tested.ok ? (
            <div className="rounded-lg border border-border p-3" data-testid="onboarding-db-error">
              <div className="flex items-center gap-2">
                <StatusPill tone="danger">{t("status.error")}</StatusPill>
                <span className="text-sm text-foreground">{errorCopy(tested.errorCode)}</span>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="outline"
            onClick={runTest}
            disabled={!credsComplete || validate.isPending}
            data-testid="onboarding-db-test"
          >
            {validate.isPending ? t("db.testing") : t("db.test")}
          </Button>
          <Button
            onClick={runSave}
            disabled={!credsComplete || create.isPending}
            data-testid="onboarding-db-save"
          >
            {create.isPending ? t("db.saving") : t("db.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
